import { create } from 'zustand';

export type SharedColorMode = 'light' | 'dark';

type SharedThemeSnapshot = {
  colorMode: SharedColorMode;
  themeName: string;
};

type SharedThemeUpdate = {
  colorMode?: SharedColorMode;
  themeName?: string;
};

type SharedThemeStore = SharedThemeSnapshot & {
  setTheme: (update: SharedThemeUpdate) => void;
};

const STORAGE_KEY = 'shared-theme-state';
const EVENT_NAME = 'shared:theme-updated';

const DEFAULT_THEME_STATE: SharedThemeSnapshot = {
  colorMode: 'light',
  themeName: 'default',
};

function sanitizeColorMode(value?: string): SharedColorMode {
  return value === 'dark' ? 'dark' : 'light';
}

function sanitizeThemeName(value?: string): string {
  const trimmed = (value ?? 'default').trim();
  return trimmed.length > 0 ? trimmed : 'default';
}

function normalizeThemeSnapshot(
  input?: Partial<SharedThemeSnapshot>,
): SharedThemeSnapshot {
  return {
    colorMode: sanitizeColorMode(input?.colorMode),
    themeName: sanitizeThemeName(input?.themeName),
  };
}

function areThemeSnapshotsEqual(
  left: SharedThemeSnapshot,
  right: SharedThemeSnapshot,
): boolean {
  return (
    left.colorMode === right.colorMode && left.themeName === right.themeName
  );
}

function readThemeSnapshotFromStorage(): SharedThemeSnapshot {
  if (typeof window === 'undefined') {
    return DEFAULT_THEME_STATE;
  }

  try {
    const serialized = localStorage.getItem(STORAGE_KEY);
    if (!serialized) {
      return DEFAULT_THEME_STATE;
    }

    const parsed = JSON.parse(serialized) as Partial<SharedThemeSnapshot>;
    return normalizeThemeSnapshot(parsed);
  } catch {
    return DEFAULT_THEME_STATE;
  }
}

function writeThemeSnapshotToStorage(snapshot: SharedThemeSnapshot): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Intentionally ignore localStorage write failures.
  }
}

function dispatchSharedThemeEvent(snapshot: SharedThemeSnapshot): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<SharedThemeSnapshot>(EVENT_NAME, {
      detail: snapshot,
    }),
  );
}

function applyThemeSnapshot(snapshot: SharedThemeSnapshot): void {
  const current = useSharedThemeStore.getState();
  if (areThemeSnapshotsEqual(current, snapshot)) {
    return;
  }

  useSharedThemeStore.setState(snapshot);
}

export const useSharedThemeStore = create<SharedThemeStore>((set, get) => ({
  ...readThemeSnapshotFromStorage(),
  setTheme: (update) => {
    const current = get();
    const next = normalizeThemeSnapshot({
      colorMode: update.colorMode ?? current.colorMode,
      themeName: update.themeName ?? current.themeName,
    });

    if (areThemeSnapshotsEqual(current, next)) {
      return;
    }

    set(next);
    writeThemeSnapshotToStorage(next);
    dispatchSharedThemeEvent(next);
  },
}));

let listenersInitialized = false;

function initializeSharedThemeListeners(): void {
  if (listenersInitialized || typeof window === 'undefined') {
    return;
  }

  listenersInitialized = true;

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) {
      return;
    }

    applyThemeSnapshot(readThemeSnapshotFromStorage());
  });

  window.addEventListener(EVENT_NAME, (event) => {
    const customEvent = event as CustomEvent<SharedThemeSnapshot>;
    if (!customEvent.detail) {
      return;
    }

    applyThemeSnapshot(normalizeThemeSnapshot(customEvent.detail));
  });
}

initializeSharedThemeListeners();
